import { Component, Inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import _ from 'lodash';
import { ClientLoggerService } from '../../services/client-logger.service';
import {Herd, Farm, ParnellEnum, AdminUser, CodeFileItem, 
        StatesAndProvinceItem, FarmType, BreedName } from '../../interfaces/mysynch.interfaces';
import { BroadCastService } from "../../services/broadcast.service";
import { MySynchEnumerationService } from '../../services/mysynch.enumeration.service';
import { PerformanceMetricsService } from '../../services/performance-metrics.service';
import { PerformanceContextService } from '../../services/performance-context.service';
import { PerformanceContext } from '../../interfaces/mysynch.interfaces';

interface FarmManager{
    firstName:string;
    lastName:string;
    email:string;
    phone:string;
}
interface MaintFarm{
    farmManager:FarmManager;
    farm:Partial<Farm> 
    herd:Partial<Herd> 
}

@Component({
    standalone: false,
    selector: 'admin-farm-management-maint',
    templateUrl: '../app/pages/admin-farm-management-maint/admin-farm-management-maint.view.html',
    styleUrls: ['../app/pages/admin-farm-management-maint/admin-farm-management-maint.style.css']
    })
export class AdminFarmManagementMaint implements OnInit , OnDestroy{

    @ViewChild('farmForm') farmForm: NgForm;

    private performanceContext: PerformanceContext | null = null;
    private defaultPersonTypeId:number = 6; // "Other"
    private updatePermissionCode: string = 'FARM_MANAGEMENT_MAINT_UPDATE';
    private activeHerd = {} as Herd; 
    private activeFarm = {} as Farm;
    private isTM: boolean = false;
    private codeFile:any[] = [];
    private statesAndProvinces: StatesAndProvinceItem[] = [];
    private defaultMeasurementStandardCode: string | undefined = 'IMPERIAL';
    private defaultMeasurementStandard: CodeFileItem|undefined = undefined;
    private isSaving: boolean = false;
    
    _:any = _;
    loading:	boolean = true;
    hasUpdatePermission: boolean = false;
    farm: MaintFarm = {
        farmManager: {
            firstName: "",
            lastName: "",
            email: "",
            phone: "",

        },
        farm : {
            isTestFarm: false,
            name: "",
            country: "",
            addressLine1: "",
            addressLine2: "",
            city: "",
            state: "",
            postalCode: "",
            farmTypeId: null,
            territoryManagerId: null,
            status: null,
            breedNameId: null,
            allowFertilityAudit: false,
            allowFarmGoals: false
            
        } ,
        herd: {
            herdMgmtSoftware: ""
        }
    }

    countries: CodeFileItem[] = [];
    states: CodeFileItem[] = [];

    farmTypes:FarmType[] = [];
    breedNames:BreedName[] = [];
    territoryManagers:AdminUser[] = [];
    farmStatuses: ParnellEnum[] = [];
    herdManagementSoftwareList:CodeFileItem[] = [];
    measurementStandards:CodeFileItem[] = [];
    selectedMeasurementStandard = {} as CodeFileItem;

    isEditing:boolean = true;
    // the farm id of the farm that we are editing , but will be undefined if we are adding a new farm
    farmId:number|undefined = undefined;
    // if we came from the territory manager dashboard , we want to return the user to the tab they were on
    tmDashSelectedTabName:string|undefined = undefined;
    constructor(@Inject('MySynchFactory') private MSF:any,
            @Inject('ApiFactory') private api:any,
            @Inject('ViewAuthorizationFactory') private viewAuth:any,
            @Inject('F360Constant') private f360Constant:any,
            @Inject(BroadCastService) private broadCastSrv:BroadCastService, 
            @Inject(MySynchEnumerationService) private mySynchEnumSvc:MySynchEnumerationService,
            @Inject(PerformanceMetricsService) private perfMetricSvc:PerformanceMetricsService,
            @Inject(PerformanceContextService) private perfContextSvc:PerformanceContextService,
            @Inject(ClientLoggerService) private log:ClientLoggerService){

           }   

    ngOnInit(): void {
        this.MSF.ready(() => {
            this.activeHerd = _.cloneDeep<Herd>(this.MSF.getActiveHerd());
            this.activeFarm = _.cloneDeep<Farm>(this.MSF.getActiveFarm());
            this.isTM = this.viewAuth.hasRole(this.mySynchEnumSvc.getMySynchEnumeration('Role','TERRITORY_MANAGER'));
            this.farmTypes = this.MSF.lists.farmType;
           
            this.farmStatuses  = _.reject(_.values(this.mySynchEnumSvc.getMySynchEnumerationItems("FarmStatus")),{name : 'PENDING'});
            this.performanceContext = this.perfContextSvc.createPerformanceContext();
            this.perfMetricSvc.startTiming("Page:FarmManagementMaint:Render");
            // if farmId on state.params is an empty string then we are adding a new farm
            if(_.isNull(this.MSF.state.params.farmId) || _.isEqual(this.MSF.state.params.farmId,"")){
                this.isEditing = false;
            }else{
                this.farmId = Number(this.MSF.state.params.farmId);
            }
            const tmDashSelectedTabName = _.get(this.MSF,'state.params.tmDashSelectedTabName') as string;
            this.tmDashSelectedTabName = _.isUndefined(tmDashSelectedTabName) ? "active-farms" : tmDashSelectedTabName;
            
            this.codeFile =  _.cloneDeep(_.get(this.MSF,"lists.codeFile",[])); 


            this.hasUpdatePermission = this.viewAuth.hasPermission(this.mySynchEnumSvc.getMySynchEnumeration('Permission',this.updatePermissionCode));
            this.loadReportData().then(() => {
                this.loadSelectOptions();
                this.loading = false;
                this.perfMetricSvc.markPageReady("Page:FarmManagementMaint:Render", this.performanceContext);
            });
        });
    }
    ngOnDestroy(): void {
        this.performanceContext = null;
    }

    private loadSelectOptions(){
        this.perfMetricSvc.startTiming('Page:FarmManagementMaint:Build');
        this.loadHerdManagementSoftwareList();	

        this.loadCountryList();

        this.loadStateList();
        this.loadMeasurementStandardList();
        this.loadBreedNameList();
        this.loadTerritoryManagerList();

        this.perfMetricSvc.endTiming("page",'Page:FarmManagementMaint:Build', this.performanceContext);
    }  

    private loadHerdManagementSoftwareList(){
        this.herdManagementSoftwareList = _.sortByOrder(_.filter(this.codeFile,{codeType: "HERDMGMTSW"}),["fullName"]);  
    }

    private loadCountryList(){
        this.countries = _.filter(this.codeFile,{codeType: "COUNTRY"});
    }
    private loadBreedNameList(){
        this.breedNames = _.sortByOrder(this.MSF.lists.breedName,["dropDownSeq"]);
    }

    private loadTerritoryManagerList(){
        this.territoryManagers = _.sortByOrder(this.MSF.lists.personTM,["dropDownSeq"]);
    }

    // function that loads $s.satesAndProvinces with available countries and their states(or Provinces)
    // and then sets the state dropdown options to the states\provinces for the farm's country
    private loadStateList(){
        const countries: CodeFileItem[] = _.filter(this.codeFile,{codeType: "COUNTRY"}); 
        
        const allStatesAndProvinces: CodeFileItem[] = this.MSF.getCodeFileSubsetWithParsedDetailsJson(_.filter(this.codeFile,(codeFileItem)=>{
            var codeType = _.get(codeFileItem,'codeType');
            return _.isEqual(codeType,'STATE') || _.isEqual(codeType,'PROVINCE');
        }));

        _.forEach(countries,(country)=>{
            
            const countryCode: string = _.get(country,'code');

            const stateList: StatesAndProvinceItem = {
                country : countryCode,
                states: _.filter(allStatesAndProvinces,(stateAndProvinceItem)=>{
                                return _.isEqual(_.get(stateAndProvinceItem,'detailsJson.country'),countryCode);
                        })
            }

            this.statesAndProvinces.push(stateList);

        });
        
        const farmCountry: string = _.get(this.farm.farm,'country');
        this.setStateList(farmCountry);

    }

    private loadMeasurementStandardList(){
        var measurementStandards = _.filter(this.codeFile,{codeType: "MEASUREMENTSTANDARD"}); 

        var parsedMeasurementStandards = this.MSF.getCodeFileSubsetWithParsedDetailsJson(measurementStandards);
        
        this.measurementStandards = parsedMeasurementStandards;

        this.defaultMeasurementStandard = _.find(parsedMeasurementStandards,{code: this.defaultMeasurementStandardCode}) as CodeFileItem;
        
        // if we are not editting an existing farm , 
        //setting default measurement standard for the new farm.
        if(!this.isEditing){
            this.setMeasurementStandard(this.defaultMeasurementStandard);
            this.selectedMeasurementStandard = this.defaultMeasurementStandard;

        }else{
            var currentMeasurementStandardCode = _.get(this.farm.farm,'measurementStandard');
            var currentMeasurementStandard = _.find(this.measurementStandards,{code: currentMeasurementStandardCode}) as CodeFileItem;
            this.selectedMeasurementStandard = currentMeasurementStandard;
        }
    }


        // sets the options available in the states dropdown
    private setStateList(country: string){
            
        var states = _.get(_.find(this.statesAndProvinces,{country: country}),'states',[]) ;

        this.states = states;
        
    }

    private setMeasurementStandard(measurementStandard: CodeFileItem){
        var measurementStandardCode  = _.get(measurementStandard,'code');
        
        _.set(this.farm.farm,'measurementStandard',measurementStandardCode);
    }

    private loadReportData(){
        return new Promise<void>((resolve) => {

            if(this.isEditing){
            this.perfMetricSvc.startTiming("Page:FarmManagementEditFarmGetFarmById:Request");
            this.api.getFarmById(this.farmId, this.performanceContext, (err:any, data:any) => {
                this.perfMetricSvc.endTiming("page","Page:FarmManagementEditFarmGetFarmById:Request", this.performanceContext);
                    if (err) {
                        this.log.error('unable to get farm with Id of '+this.farmId+': ' + err);
                
                        return;
                    } 
                    
                    this.farm = _.clone(data);
                    resolve();

                });
            }else{
                 resolve();
            }	 
        }); 

    }


    // on change of the country field, we want to reload the state\province options
    onCountryChange(country: string){

        this.setStateList(country);
    }

    updateHerdManagementSoftware(){
            
        if(_.isEqual(this.activeHerd.herdId,this.farm.herd.herdId)){
            _.set(this.activeHerd,'herdMgmtSoftware',this.farm.herd.herdMgmtSoftware);
        }
        

    } 


    allowFertilityAuditChanged(){
        
        var allowFertilityAudit = _.get(this.farm,'farm.allowFertilityAudit');

        if(!_.isNull(allowFertilityAudit) && !_.isUndefined(allowFertilityAudit) && _.isEqual(allowFertilityAudit,true)){
            ///_.set(this.farm,'farm.allowTrendsAndTargets',true);
            _.set(this.farm,'farm.allowFarmGoals',true);
        }

    }

    allowFarmGoalsChanged()
    {
        var allowFertilityAudit = _.get(this.farm,'farm.allowFertilityAudit');
        var allowFarmGoals = _.get(this.farm,'farm.allowFarmGoals');
        
        if(!_.isNull(allowFarmGoals) && !_.isUndefined(allowFarmGoals) && !_.isEqual(allowFertilityAudit,true)){
        _.set(this.farm,'farm.allowFarmGoals',allowFarmGoals);
        }
    }

    submit(){

        if(this.isEditing){
            this.updateFarm();
        }else{
            this.createFarmAndPerson();
        }
    }


    private createFarmAndPerson() {
        if (!this.farmForm.form.valid) {
                this.farmForm.form.markAllAsTouched();
            return false;
        }

        this.isSaving = true;

        let createData = {
            farmManager: "",
            farm: "",
            herd: "",
            territoryManager: ""
        }
        _.set(createData,'farmManager',JSON.stringify(this.farm.farmManager));
        _.set(createData,'farm',JSON.stringify(this.farm.farm));
        _.set(createData,'herd',JSON.stringify(this.farm.herd));
        _.set(createData, 'territoryManager', _.find(this.territoryManagers,{personId: this.farm.farm.territoryManagerId}))
        this.log.info("creating farm,farm manager, and herd with the following data");
        this.log.info(createData);

        this.api.adminCreateFarm(createData, (err: any, newFarmAndPerson: any) => {
            this.loading = false;

            if (err) {
                this.isSaving = false;
                return this.log.error(this.MSF.notify.danger('Error creating farm and person ' + err));
            }

            var availableFarms = this.MSF.getAvailableFarms();

            availableFarms.push(newFarmAndPerson.farm);

            var sortedAvailableFarms = this.MSF.getSortedListOfAvailableFarms(availableFarms);
            
            this.MSF.setAvailableFarms(sortedAvailableFarms);

            this.MSF.updateAvailableHerdListOptions().then((promiseHerdData)=>{
                this.MSF.updateAdditionalHerdDData().then((promiseAdditionalHerdData)=>{

                
                this.broadCastSrv.sendFarmCreatedMsg(newFarmAndPerson.farm);


                this.log.info(this.MSF.notify.success('Farm and Farm Admin created successfully, email sent to Farm Admin'));
                this.MSF.loadPage('FarmManagement');
                });
            })
        });

                
    };

    onMeasurementStandardChange(selectedMeasurementStandard: CodeFileItem){
        this.selectedMeasurementStandard = selectedMeasurementStandard;
        this.setMeasurementStandard(this.selectedMeasurementStandard);
    }

    updateFarm() {

        if (!this.farmForm.form.valid) {
            this.MSF.touchForm(this.farmForm);
            return false;
        }

        this.isSaving = true;
    
        let updateData = {
            farmManager: "",
            farm: "",
            herd: ""
        }
        
        _.set(this.farm.herd,'name',this.farm.farm.name);
        // removing the latestCowfileSizesJson from the herd object before updating the farm. LatestCowfileSizesJson is not updated through this API and can be large.
        const cleanedHerd = _.cloneDeep(_.omit(this.farm.herd,['latestCowfileSizesJson']));
        _.set(updateData,'farmManager',JSON.stringify(this.farm.farmManager));
        _.set(updateData,'farm',JSON.stringify(this.farm.farm));
        _.set(updateData,'herd',JSON.stringify(cleanedHerd));

        this.log.info("updating farm with the following data");
        this.log.info(updateData);

        this.api.adminUpdateFarm(this.farmId,updateData,(err: any, data: any) => {
        if (err) {
            this.log.error('failed to update farm with Id : '+this.farmId+' '+ err);
            this.isSaving = false;
            return;
        }
            const inactiveFarmStatusValue = this.mySynchEnumSvc.getMySynchEnumerationValue('FarmStatus','INACTIVE');
        // updating the active farm properties in mysynch factory and cookies.state.activeFarm   
        if(_.isEqual(this.activeFarm.farmId,this.farm.farm.farmId)){

                var updateActiveFarm = _.merge(this.activeFarm,this.farm.farm);
            // if the farm's status has changed to inactive, then it will be removed from the available farms list in updateAvailableFarmListOption
            if(_.isEqual(_.get(updateActiveFarm,'status'),inactiveFarmStatusValue)){
                this.MSF.updateAvailableFarmListOption(updateActiveFarm,true);
                this.MSF.setFirstAvailableFarmActive();
            }else{
                this.MSF.setActiveFarm(updateActiveFarm);
            }  
                
            
        }else{
                // updating the available farms local storage item , setActiveFarm 
                //also updates the available farm list option when the active farm is being edited
                if(_.isEqual(_.get(this.farm.farm,'status'),inactiveFarmStatusValue)){
                    this.MSF.updateAvailableFarmListOption(this.farm.farm,true);
                }else{
                    this.MSF.updateAvailableFarmListOption(this.farm.farm,false);
                }
        }


            // update the active herd's HMS in both the database and its entry in cookies.state.
        if(_.isEqual(this.activeHerd.herdId,this.farm.herd.herdId)){
            var updateActiveHerd = _.merge(this.activeHerd,this.farm.herd);
            // if the farm's status has changed to inactive, then the herd will be removed from the available herds list in updateAvailableHerdListOption
            if(_.isEqual(_.get(updateActiveFarm,'status'),inactiveFarmStatusValue)){
                    this.MSF.updateAvailableHerdListOption(updateActiveHerd,true); 
                    this.MSF.setFirstAvailableHerdActive();
                    this.MSF.updateAdditoinalHerdDataListOption(_.get(updateActiveHerd,'herdId'),true);
            }else{
                this.MSF.setActiveHerd(updateActiveHerd,false);
                if(!_.isUndefined(_.get(updateActiveHerd,'herdMgmtSoftware'))){
                    _.set(this.MSF.cookies.state,'activeFarm.herds[0].herdMgmtSoftware',_.get(updateActiveHerd,'herdMgmtSoftware'));
                    _.set(this.MSF.cookies.state,'activeFarm.herds[0].name',_.get(updateActiveFarm,'name'));
                    this.MSF.cookies.save();
                    _.set(updateActiveFarm,'herds[0].herdMgmtSoftware',_.get(updateActiveHerd,'herdMgmtSoftware'));
                    _.set(updateActiveFarm,'herds[0].name',_.get(updateActiveFarm,'name'));
                }
                this.MSF.updateAvailableHerdListOption(updateActiveHerd,false);
            }
                
                this.log.info(this.MSF.notify.success('Farm was updated successfully'));
                if(this.isTM){
                    this.MSF.loadPage('TerritoryDashboard',{selectedTabName:"active-farms"});
                }else{
                    this.MSF.loadPage('FarmManagement');
                }
                
                
                this.broadCastSrv.sendFarmUpdatedMsg(updateActiveFarm);
        }else{
            this.log.info(this.MSF.notify.success('Farm was updated successfully'));
            if(_.isEqual(_.get(this.farm.farm,'status'),inactiveFarmStatusValue)){
                this.MSF.updateAvailableHerdListOption(this.farm.herd,true);
                this.MSF.updateAdditoinalHerdDataListOption(this.farm.herd.herdId,true);
            }else{
                this.MSF.updateAvailableHerdListOption(this.farm.herd,false);
                this.MSF.updateAdditoinalHerdDataListOption(this.farm.herd.herdId,false);
            }

            if(this.isTM){
                this.MSF.loadPage('TerritoryDashboard',{selectedTabName:this.tmDashSelectedTabName});
                }else{
                    this.MSF.loadPage('FarmManagement');
                }
                
                this.broadCastSrv.sendFarmUpdatedMsg("");
        }
        
    
        });  

        
    }

    cancel() {
        if(this.isTM){
            this.MSF.loadPage('TerritoryDashboard',{selectedTabName:this.tmDashSelectedTabName});
            }else{
                this.MSF.loadPage('FarmManagement');
            }
    };

}